import React from 'react';
import { Icon, IconName } from '@usealloy/component-library';
import type { FraudSignalIndicator } from '~/entities/FeatureImportanceWidget/types';
import BaseNode from '~/dashboard/views/workflows/editor/components/Graph/nodes/BaseNode/BaseNode';

const getIndicatorIcon = (score: number) => {
  if (score > 0.25) {
    return <Icon icon={IconName.GaugeHigh} color="var(--cl-red-500)" />;
  }
  return <Icon icon={IconName.GaugeLow} color="var(--cl-green-400)" />;
};

interface FraudSignalIndicatorsProps {
  indicators: FraudSignalIndicator[];
  score: number;
  twoColumns?: boolean;
  selectedIndicatorName?: string | null;
  onIndicatorClick?: (indicator: FraudSignalIndicator) => void;
}

export const FraudSignalIndicators = ({
  indicators,
  score,
  twoColumns = false,
  selectedIndicatorName,
  onIndicatorClick,
}: FraudSignalIndicatorsProps) => {
  const indicatorIcon = getIndicatorIcon(score);
  const containerStyle = twoColumns
    ? {
      display: 'grid' as const,
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
      gap: 'var(--cl-space-2)',
    }
    : {
      display: 'flex' as const,
      flexDirection: 'column' as const,
      gap: 'var(--cl-space-1)',
    };

  const baseNodeStyle = {
    fontSize: 'var(--cl-font-size-2)',
    fontWeight: 'var(--cl-font-weight-regular)',
    lineHeight: 'var(--cl-line-height-2)',
    color: 'var(--cl-text-default)',
    ...(twoColumns && {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap' as const,
      minWidth: 0,
    }),
  };

  const isInteractive = !!onIndicatorClick;

  return (
    <div style={containerStyle}>
      {indicators.map(indicator => {
        const isSelected = selectedIndicatorName === indicator.name;
        return (
          <div
            key={indicator.name}
            role={isInteractive ? 'button' : undefined}
            tabIndex={isInteractive ? 0 : undefined}
            onClick={() => onIndicatorClick?.(indicator)}
            onKeyDown={e => {
              if (isInteractive && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                onIndicatorClick?.(indicator);
              }
            }}
            style={{
              ...(twoColumns ? { minWidth: 0, overflow: 'hidden' } : undefined),
              ...(isInteractive && { cursor: 'pointer' }),
              ...(isSelected && {
                borderRadius: '4px',
                border: '1px solid var(--border-border-focused-juicyblue-600, #1D4ED8)',
                boxShadow: '0 0 0 4px rgba(39, 85, 207, 0.20)',
              }),
            }}
            title={twoColumns ? indicator.name : undefined}
          >
            <BaseNode
              stateStyle={{ baseBackgroundColor: 'var(--cl-gray-300)' }}
              style={baseNodeStyle}
              label={indicator.name}
              icon={indicatorIcon}
              id={indicator.name}
              type={null}
              isOutsideCanvas
              disable={{
                nodeMenu: true,
                nodeMenuHoverState: true,
                nodeConnector: true,
              }}
            />
          </div>
        );
      })}
    </div>
  );
};
